/**
 * ProtectedRoute — Role-based route guard
 *
 * Wraps routes that require authentication and optionally
 * specific roles or permissions.
 *
 * Usage:
 *   <Route element={<ProtectedRoute allowedRoles={['admin']}><Outlet /></ProtectedRoute>}>
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '../../state_management_and_stores/auth.store';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  allowedRoles?: string[];
  requiredPermission?: string;
  requiredPermissions?: string[];
  anyPermission?: string[];
  fallback?: React.ReactNode;
}

export function ProtectedRoute({
  children,
  allowedRoles,
  requiredPermission,
  requiredPermissions,
  anyPermission,
  fallback,
}: ProtectedRouteProps) {
  const {
    isAuthenticated,
    isLoading,
    role,
    hasRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  } = useAuthStore();

  // Still loading auth state — show spinner
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated — redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role check
  if (allowedRoles && allowedRoles.length > 0) {
    const hasAllowedRole = allowedRoles.some(
      (r) => hasRole(r) || role?.role_type === r
    );
    if (!hasAllowedRole) {
      return fallback ? <>{fallback}</> : <Navigate to="/unauthorized" replace />;
    }
  }

  // Single permission check
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return fallback ? <>{fallback}</> : <Navigate to="/unauthorized" replace />;
  }

  // All permissions required
  if (requiredPermissions && !hasAllPermissions(requiredPermissions)) {
    return fallback ? <>{fallback}</> : <Navigate to="/unauthorized" replace />;
  }

  // Any permission required
  if (anyPermission && !hasAnyPermission(anyPermission)) {
    return fallback ? <>{fallback}</> : <Navigate to="/unauthorized" replace />;
  }

  // Authorized — render children or nested routes via <Outlet />
  return children ? <>{children}</> : <Outlet />;
}
